import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { cn } from '../../lib/utils';
import { coreAPI, purchasingAPI, suppliersAPI, Site, CompanyGroup, Warehouse, SupplierRatingThreshold, CommodityGroup, CommodityType } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
// CR-4783 follow-up: share the dashboard's Quick Date Ranges helper so the
// two screens compute the same dateFrom/dateTo for a given preset.
import { getLast12MonthsRange } from '../dashboard/DashboardFilters';

export interface SupplierPerformanceFilterValues {
  division: string;
  site: string;
  supplierGroup: string;
  supplierSite: string;
  supplierCode: string;
  warehouse: string;
  strategic: boolean;
  hideChildCompanies: boolean;
  commodityGroup: string;
  commodityType: string;
  timePeriod: string;
  dateFrom: string; // YYYY-MM-DD format
  dateTo: string; // YYYY-MM-DD format
  orderType: string[];
  supplierType: string;
  rating: string;
  excludeNARatings: boolean;
  dataMode: 'historical' | 'live';
  // CR-4783: Geographic filters
  siteCountry: string;
  supplierCountry: string;
}

const DEFAULT_FILTER_VALUES: SupplierPerformanceFilterValues = {
  division: 'all',
  site: 'all',
  supplierGroup: 'all',
  supplierSite: 'all',
  supplierCode: '',
  warehouse: 'all',
  strategic: false,
  hideChildCompanies: false,
  commodityGroup: 'all',
  commodityType: 'all',
  timePeriod: 'all',
  dateFrom: '',
  dateTo: '',
  orderType: [],
  supplierType: 'all',
  rating: 'all',
  excludeNARatings: true,
  dataMode: 'historical',
  siteCountry: 'all',
  supplierCountry: 'all',
};

interface SupplierPerformanceFiltersProps {
  filters: SupplierPerformanceFilterValues;
  onApplyFilters: (filters: SupplierPerformanceFilterValues) => void;
  ratingThresholds: SupplierRatingThreshold[];
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type QuickRangeKey = 'last12Months' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear' | 'lastYear';

function datesForQuickRange(range: QuickRangeKey): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'last12Months':
      return getLast12MonthsRange();
    case 'thisMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
  }
}

const ORDER_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'blanket', label: 'Blanket' },
  { value: 'contract', label: 'Contract' },
  { value: 'spot', label: 'Spot Buy' },
  { value: 'consignment', label: 'Consignment' },
];

// CR-4783: Supplier Type now segments by organisational relationship
// (same group vs different group relative to the viewing customer).
const SUPPLIER_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

/** Extract array from either a flat array response or paginated {results} response */
function extractResults<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in (data as Record<string, unknown>)) {
    return ((data as Record<string, unknown>)['results'] as T[]) || [];
  }
  return [];
}

export default function SupplierPerformanceFilters({
  filters,
  onApplyFilters,
  ratingThresholds,
}: SupplierPerformanceFiltersProps) {
  const { user } = useAuth();
  // CR-4783 round 2: divisions are *customer-side CompanyGroups* (e.g.
  // "Acme – Aerospace Systems"), not customer companies / sites. The round 1
  // fix sourced this list from getAccessibleCompanies which returns sites
  // (e.g. "Acme – Aerospace – UK | Daresbury") so the dropdown read as a
  // site picker rather than a division picker, exactly as Sarah flagged.
  const [divisions, setDivisions] = useState<CompanyGroup[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [supplierSites, setSupplierSites] = useState<Site[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<CompanyGroup[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [_loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false); // Start collapsed for cleaner UX

  // Local pending filter state — only synced to parent when Apply or Clear is clicked
  const [localFilters, setLocalFilters] = useState<SupplierPerformanceFilterValues>(filters);

  // Sync local state if parent resets filters externally (e.g. from Clear All)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const isHelixUser = user?.platform === 'HELIX';

        // Fetch commodity data (available to all users)
        const commodityPromises = [
          purchasingAPI.getCommodityGroups({ enabled: true, page_size: 100 }).catch(() => ({ data: [] })),
          purchasingAPI.getCommodityTypes({ enabled: true, page_size: 200 }).catch(() => ({ data: [] })),
        ] as const;

        if (isHelixUser) {
          // Helix users can access all data
          const [customerRes, supplierRes, groupsResponse, warehousesResponse, customerGroupsResponse, commodityGroupsResponse, commodityTypesResponse] = await Promise.all([
            coreAPI.getCompanies({ company_type: 'customer', is_active: true }),
            coreAPI.getCompanies({ company_type: 'supplier', is_active: true, page_size: 1000 }),
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'supplier' }),
            coreAPI.getWarehouses({ is_active: true }),
            // CR-4783 round 2: divisions = customer-side CompanyGroups.
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'customer' }).catch(() => ({ data: { results: [] } })),
            ...commodityPromises,
          ]);
          const customerCompanies = customerRes.data.results || [];
          const supplierCompanies = supplierRes.data.results || [];
          setSites(customerCompanies as unknown as Site[]);
          setSupplierSites(supplierCompanies as unknown as Site[]);
          setSupplierGroups(groupsResponse.data.results || []);
          setWarehouses(warehousesResponse.data.results || []);
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(extractResults(commodityTypesResponse.data));
          const customerDivisions = customerGroupsResponse.data.results || [];
          setDivisions(customerDivisions.length > 1 ? customerDivisions : []);
        } else {
          // Non-Helix users use accessible endpoints only.
          // CR-4887: supplier-portal users want CUSTOMER hierarchy in the
          // Company Site / Division dropdowns. The Supplier Performance
          // page mirrors the Dashboard's filter panel; flip the accessible
          // endpoints with ?context=customer_filter for the same reason.
          const isSupplierPortal = user?.platform === 'SUPPLIER';
          const filterContext = isSupplierPortal ? { context: 'customer_filter' as const } : undefined;
          const [sitesResponse, groupsResponse, customerGroupsResponse, suppliersResponse, commodityGroupsResponse, commodityTypesResponse] = await Promise.all([
            coreAPI.getAccessibleSites(filterContext).catch(() => ({ data: [] })),
            coreAPI.getSupplierGroupsForFilter(filterContext).catch(() => ({ data: { results: [] } })),
            // CR-4783 round 2: divisions = customer-side CompanyGroups, not
            // accessible companies. Round 1 used getAccessibleCompanies which
            // returned customer SITES (e.g. "Acme – Aerospace – UK |
            // Daresbury"); the dropdown should list DIVISIONS (e.g. "Acme –
            // Aerospace Systems"). for-filter respects scope and the same
            // customer_filter context flip the supplier portal already uses
            // for the supplier-group dropdown, so the call shape mirrors the
            // sibling above.
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'customer' }).catch(() => ({ data: { results: [] } })),
            suppliersAPI.getProfiles({ page_size: 1000, minimal: true }).catch(() => ({ data: { results: [] } })),
            ...commodityPromises,
          ]);
          const accessibleSites = Array.isArray(sitesResponse.data) ? sitesResponse.data : [];
          setSites(accessibleSites);
          const supplierCompanies = (suppliersResponse.data.results || []).map(s => ({
            id: s.company_id,
            name: s.company_name,
          })) as unknown as Site[];
          setSupplierSites(supplierCompanies);
          setSupplierGroups(groupsResponse.data.results || []);
          setWarehouses([]); // Warehouses not available for non-Helix users
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(extractResults(commodityTypesResponse.data));
          const customerDivisions = customerGroupsResponse.data.results || [];
          setDivisions(customerDivisions.length > 1 ? customerDivisions : []);
        }
      } catch (error) {
        // Silently handle - filters will just be empty
        console.debug('Filter options not available');
      } finally {
        setLoading(false);
      }
    };
    fetchFilterOptions();
  }, [user?.platform]);

  const updateFilter = <K extends keyof SupplierPerformanceFilterValues>(
    key: K,
    value: SupplierPerformanceFilterValues[K]
  ) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // CR-4783 round 3: scope the Division dropdown to the user's accessible
  // hierarchy. Round 2 listed every customer-side CompanyGroup globally, so
  // a user logged in at Acme – Automotive – Germany still saw Acme – Aerospace
  // and Acme – Industrial in the dropdown. Filter divisions to the unique
  // group_ids carried by the accessible sites the FE already loaded for the
  // Company Site dropdown. Helix users see everything (sites is the full
  // customer list anyway, so the filter is a no-op for them).
  const scopedDivisions = useMemo(() => {
    if (user?.platform === 'HELIX') return divisions;
    const accessibleGroupIds = new Set(
      sites
        .map((s) => s.group_id)
        .filter((g): g is number => typeof g === 'number'),
    );
    if (accessibleGroupIds.size === 0) return divisions;
    return divisions.filter((d) => accessibleGroupIds.has(d.id));
  }, [divisions, sites, user?.platform]);

  // CR-4783 round 3: Quick Date Range now auto-applies. Sarah's feedback
  // was that clicking a preset changed the dateFrom/dateTo inputs but didn't
  // touch the data underneath until Apply Filters was pressed — which felt
  // broken. The dashboard preset row applies on click; mirror that here.
  const applyQuickRange = (range: QuickRangeKey) => {
    const { dateFrom, dateTo } = datesForQuickRange(range);
    const next = { ...localFilters, dateFrom, dateTo, timePeriod: range };
    setLocalFilters(next);
    onApplyFilters(next);
  };

  // CR-4783 round 3: country dropdowns auto-apply too — same usability
  // complaint as Quick Date Ranges.
  const applyAndUpdate = <K extends keyof SupplierPerformanceFilterValues>(
    key: K,
    value: SupplierPerformanceFilterValues[K],
  ) => {
    const next = { ...localFilters, [key]: value };
    setLocalFilters(next);
    onApplyFilters(next);
  };

  const activeQuickRange: QuickRangeKey | null = (
    ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'] as QuickRangeKey[]
  ).find((range) => {
    const d = datesForQuickRange(range);
    return d.dateFrom === localFilters.dateFrom && d.dateTo === localFilters.dateTo;
  }) ?? null;

  const toggleOrderType = (orderType: string) => {
    setLocalFilters(prev => {
      const currentTypes = prev.orderType;
      return {
        ...prev,
        orderType: currentTypes.includes(orderType)
          ? currentTypes.filter(t => t !== orderType)
          : [...currentTypes, orderType],
      };
    });
  };

  const clearFilters = () => {
    setLocalFilters(DEFAULT_FILTER_VALUES);
    onApplyFilters(DEFAULT_FILTER_VALUES);
  };

  const activeFilterCount = Object.entries(localFilters).filter(([key, value]) => {
    if (key === 'excludeNARatings') return !value; // Count if unchecked (default is true)
    if (key === 'strategic' || key === 'hideChildCompanies') return value === true;
    if (key === 'orderType') return (value as string[]).length > 0;
    if (key === 'dateFrom' || key === 'dateTo') return value !== '';
    if (key === 'dataMode') return value !== 'historical';
    return value !== 'all' && value !== '';
  }).length;

  return (
    <Card className={cn("mb-4", !expanded && "bg-muted/30")}>
      <CardContent className={cn("p-4", !expanded && "pb-4")}>
        {/* Filter Header - Always visible */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className={cn(
              "p-2 rounded-lg",
              expanded ? "bg-primary text-primary-foreground" : "bg-muted"
            )}>
              <Filter className="h-5 w-5" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">Filters</h3>
                {activeFilterCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {expanded ? 'Click to collapse' : 'Click to expand filter options'}
              </p>
            </div>
            <div className={cn(
              "ml-2 p-1 rounded transition-colors",
              expanded ? "bg-primary/10" : "bg-muted"
            )}>
              {expanded ? (
                <ChevronUp className="h-5 w-5 text-primary" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </button>

          {/* Action buttons - always visible */}
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <>
            {/* CR-4783: Quick Date Ranges — same button row used on the
                main Dashboard so users see a consistent control across
                screens. Selecting a preset populates dateFrom/dateTo. */}
            <div className="mb-6">
              <label className="text-sm font-semibold mb-3 block text-foreground">Quick Date Ranges</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyQuickRange('last12Months')}
                  className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                    activeQuickRange === 'last12Months'
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  Last 12 Months (Default)
                </button>
                {(['thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'] as QuickRangeKey[]).map((range) => {
                  const isActive = activeQuickRange === range;
                  const label = range === 'thisMonth'
                    ? 'This Month (Live)'
                    : range.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
                  return (
                    <button
                      key={range}
                      type="button"
                      onClick={() => applyQuickRange(range)}
                      className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                          : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t mb-6"></div>

            {/* CR-4593: Row 1 - Company Site, Supplier Group, Supplier Site, Checkboxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
              {/* CR-4783: Division filter — label aligned with the dashboard's
                  'Division' filter so the two screens read consistently.
                  Round 3: render scopedDivisions, not the raw all-tenants list. */}
              {scopedDivisions.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="division" className="text-xs">Division</Label>
                  <SearchableSelect
                    options={[{ value: 'all', label: 'All Divisions' }, ...scopedDivisions.map((d) => ({ value: d.id.toString(), label: d.name }))]}
                    value={localFilters.division}
                    onChange={(val) => updateFilter('division', val || 'all')}
                    placeholder="All Divisions"
                  />
                </div>
              )}

              {/* Company Site (secondary) - when no divisions */}
              {scopedDivisions.length === 0 && <div className="space-y-1.5">
                <Label htmlFor="site" className="text-xs">Company Site</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Company Sites' }, ...sites.map((s) => ({ value: s.id.toString(), label: s.name }))]}
                  value={localFilters.site}
                  onChange={(val) => updateFilter('site', val || 'all')}
                  placeholder="All Company Sites"
                />
              </div>}

              {/* Supplier Group */}
              <div className="space-y-1.5">
                <Label htmlFor="supplierGroup" className="text-xs">Supplier Group</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Supplier Groups' }, ...supplierGroups.map((g) => ({ value: g.id.toString(), label: g.name }))]}
                  value={localFilters.supplierGroup}
                  onChange={(val) => updateFilter('supplierGroup', val || 'all')}
                  placeholder="All Supplier Groups"
                />
              </div>

              {/* Supplier Site */}
              <div className="space-y-1.5">
                <Label htmlFor="supplierSite" className="text-xs">Supplier Site</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Supplier Sites' }, ...supplierSites.map((s) => ({ value: s.id.toString(), label: `${s.name} (${s.company_name})` }))]}
                  value={localFilters.supplierSite}
                  onChange={(val) => updateFilter('supplierSite', val || 'all')}
                  placeholder="All Supplier Sites"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-2 pt-5">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="strategic"
                    checked={localFilters.strategic}
                    onCheckedChange={(checked) => updateFilter('strategic', checked as boolean)}
                  />
                  <Label htmlFor="strategic" className="text-xs cursor-pointer">Strategic</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hideChildCompanies"
                    checked={localFilters.hideChildCompanies}
                    onCheckedChange={(checked) => updateFilter('hideChildCompanies', checked as boolean)}
                  />
                  <Label htmlFor="hideChildCompanies" className="text-xs cursor-pointer">Hide Child Companies</Label>
                </div>
              </div>

              {/* Warehouse */}
              <div className="space-y-1.5">
                <Label htmlFor="warehouse" className="text-xs">Warehouse</Label>
                <Select
                  value={localFilters.warehouse}
                  onValueChange={(value) => updateFilter('warehouse', value)}
                >
                  <SelectTrigger id="warehouse" className="h-9">
                    <SelectValue placeholder="All Warehouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id.toString()}>
                        {warehouse.name} ({warehouse.site_name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Commodity Group */}
              <div className="space-y-1.5">
                <Label htmlFor="commodityGroup" className="text-xs">
                  Commodity Group
                </Label>
                <Select
                  value={localFilters.commodityGroup}
                  onValueChange={(value) => {
                    updateFilter('commodityGroup', value);
                    // Reset commodity type when group changes
                    updateFilter('commodityType', 'all');
                  }}
                >
                  <SelectTrigger id="commodityGroup" className="h-9">
                    <SelectValue placeholder="All Commodities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Commodities</SelectItem>
                    {commodityGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Commodity Type */}
              <div className="space-y-1.5">
                <Label htmlFor="commodityType" className="text-xs">
                  Commodity Type
                </Label>
                <Select
                  value={localFilters.commodityType}
                  onValueChange={(value) => updateFilter('commodityType', value)}
                  disabled={localFilters.commodityGroup === 'all'}
                >
                  <SelectTrigger id="commodityType" className="h-9">
                    <SelectValue placeholder={localFilters.commodityGroup === 'all' ? 'Select group first' : 'All Types'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {commodityTypes
                      .filter((type) => localFilters.commodityGroup === 'all' || type.commodity_group.toString() === localFilters.commodityGroup)
                      .map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Date Range, Order Type, Supplier Type, Countries, Rating.
                CR-4783: Time Period dropdown removed — replaced by the
                Quick Date Ranges button row above. */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
              {/* Date From */}
              <div className="space-y-1.5">
                <Label htmlFor="dateFrom" className="text-xs">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Date To */}
              <div className="space-y-1.5">
                <Label htmlFor="dateTo" className="text-xs">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Order Type Multi-select */}
              <div className="space-y-1.5">
                <Label className="text-xs">Order Type</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-9 justify-between font-normal"
                    >
                      <span className="truncate">
                        {localFilters.orderType.length === 0
                          ? 'All Types'
                          : `${localFilters.orderType.length} selected`}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2">
                    <div className="space-y-2">
                      {ORDER_TYPES.map((type) => (
                        <div key={type.value} className="flex items-center gap-2">
                          <Checkbox
                            id={`order-type-${type.value}`}
                            checked={localFilters.orderType.includes(type.value)}
                            onCheckedChange={() => toggleOrderType(type.value)}
                          />
                          <Label
                            htmlFor={`order-type-${type.value}`}
                            className="text-sm cursor-pointer"
                          >
                            {type.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Supplier Type */}
              <div className="space-y-1.5">
                <Label htmlFor="supplierType" className="text-xs">Supplier Type</Label>
                <Select
                  value={localFilters.supplierType}
                  onValueChange={(value) => updateFilter('supplierType', value)}
                >
                  <SelectTrigger id="supplierType" className="h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLIER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CR-4783: Company Country — derived from accessible sites */}
              <div className="space-y-1.5">
                <Label htmlFor="siteCountry" className="text-xs">Company Country</Label>
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Countries' },
                    ...Array.from(new Set(sites.map((s) => s.country).filter(Boolean)))
                      .sort()
                      .map((c) => ({ value: c, label: c })),
                  ]}
                  value={localFilters.siteCountry}
                  onChange={(val) => applyAndUpdate('siteCountry', val || 'all')}
                  placeholder="All Countries"
                />
              </div>

              {/* CR-4783: Supplier Country — derived from accessible supplier sites */}
              <div className="space-y-1.5">
                <Label htmlFor="supplierCountry" className="text-xs">Supplier Country</Label>
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Countries' },
                    ...Array.from(new Set(supplierSites.map((s) => s.country).filter(Boolean)))
                      .sort()
                      .map((c) => ({ value: c, label: c })),
                  ]}
                  value={localFilters.supplierCountry}
                  onChange={(val) => applyAndUpdate('supplierCountry', val || 'all')}
                  placeholder="All Countries"
                />
              </div>

              {/* Rating */}
              <div className="space-y-1.5">
                <Label htmlFor="rating" className="text-xs">Rating</Label>
                <Select
                  value={localFilters.rating}
                  onValueChange={(value) => {
                    if (value === 'N/A') {
                      setLocalFilters(prev => ({ ...prev, rating: value, excludeNARatings: false }));
                    } else {
                      updateFilter('rating', value);
                    }
                  }}
                >
                  <SelectTrigger id="rating" className="h-9">
                    <SelectValue placeholder="All Ratings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ratings</SelectItem>
                    {ratingThresholds.length > 0 ? (
                      ratingThresholds.map((threshold) => (
                        <SelectItem key={threshold.id} value={threshold.grade}>
                          {threshold.display_label || threshold.label || threshold.grade}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="A">A (90-100%)</SelectItem>
                        <SelectItem value="B">B (75-90%)</SelectItem>
                        <SelectItem value="C">C (60-75%)</SelectItem>
                        <SelectItem value="D">D (40-60%)</SelectItem>
                        <SelectItem value="F">F (0-40%)</SelectItem>
                      </>
                    )}
                    <SelectItem value="N/A">N/A (No Data)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3: Additional options and action buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
              <div className="flex items-center gap-6">
                {/* Exclude N/A Ratings */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="excludeNARatings"
                    checked={localFilters.excludeNARatings}
                    onCheckedChange={(checked) => updateFilter('excludeNARatings', checked as boolean)}
                  />
                  <Label htmlFor="excludeNARatings" className="text-sm cursor-pointer">
                    Exclude N/A Ratings
                  </Label>
                </div>

                {/* Data Mode Toggle */}
                <div className="flex items-center gap-2 border rounded-md p-1">
                  <Button
                    variant={localFilters.dataMode === 'historical' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => updateFilter('dataMode', 'historical')}
                  >
                    Historical
                  </Button>
                  <Button
                    variant={localFilters.dataMode === 'live' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => updateFilter('dataMode', 'live')}
                  >
                    Live
                  </Button>
                </div>
              </div>

              {/* Apply Filters Button */}
              <Button
                size="sm"
                onClick={() => onApplyFilters(localFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
